import type { Project } from "../types/project";

export const projects: Project[] = [
  {
    index: "01",
    slug: "vampine-new-order",
    tabLabel: "VAMPINE",
    title: "Vampine : New Order",
    subtitle: "Original indie game demo",
    status: "DEMO RELEASED",
    year: "2026",
    engine: "UNITY / C#",
    role: "PROJECT LEAD",
    summary:
      "Awaken to a world ruled by the Intelligences and reclaim your honor! A turn-based strategy roguelike where you lead a vampire rebellion, bonds powerful servants, craft tactical card decks, and combine unique abilities to outsmart your enemies.",
    description: [
      "Completely designed and developed by our team, from the core combat loop through to the interface and presentation layer.",
      "Systems, art direction, and UI are built together in-engine so every mechanic reads the way it plays.",
    ],
    highlights: [
      "A complete gameplay loop, built to expand — new cards, enemies, and encounters drop into the existing systems.",
      "Thousands of viable deck combinations, so no two runs read the same.",
      "A dynamic audio system that reads the state of play, shifting between quiet exploration and full-intensity combat.",
      "An art direction that fuses magic and technology, ancient and far-future, into one coherent world.",
    ],
    tags: ["UNITY", "2D", "C#", "GAMEPLAY", "GAME DESIGN", "UI"],
    credits: [
      { label: "DESIGN / ART", value: "SARAH" },
      { label: "MUSIC", value: "ATOMG" },
    ],
    icon: "/projects/vampine-icon.webp",
    key: {
      src: "/projects/vampine-key-wishlist.webp",
      alt: "Vampine : New Order key art — a vampire before a red sun, with Steam and itch.io logos",
    },
    videos: [{ youtubeId: "a1nTwWMc9xY", title: "Vampine: New Order Demo Trailer" }],
    gallery: [
      {
        src: "/projects/vampine-5.webp",
        alt: "Combat — fight as the Night Lord, Vampine, against a mech enemy",
      },
      {
        src: "/projects/vampine-4.webp",
        alt: "Combat — lead humanity into rebellion with Vampine and her servants",
      },
      {
        src: "/projects/vampine-1.webp",
        alt: "Character cards — servants and Vampine forms, over hundreds of combinations",
      },
      {
        src: "/projects/vampine-2.webp",
        alt: "Enemy art — battle against a variety of Intelligences",
      },
      {
        src: "/projects/vampine-3.webp",
        alt: "Encounter art — a werewolf, a red-hooded girl, and a stitched doll",
      },
    ],
    links: [
      {
        label: "STEAM",
        href: "https://store.steampowered.com/app/5317250/Vampine_New_Order/",
        icon: "steam",
      },
      {
        label: "ITCH.IO",
        href: "https://projectdespair.itch.io/vampine-new-order-demo",
        icon: "itch",
      },
    ],
    showInTabs: true,
    webgl: {
      enabled: true,
      src: "https://play.projectdespair.com/vampine-new-order/index.html",
      aspectRatio: "16 / 9",
      note: "The WebGL build is missing some features. For the best experience, play the downloadable version.",
    },
  },
  {
    index: "02",
    slug: "togawa-sakiko-mod",
    tabLabel: "STS-SAKIKO",
    title: "Togawa Sakiko",
    subtitle: "Slay the Spire mod — custom character",
    status: "RELEASED",
    year: "2025",
    engine: "JAVA / MODTHESPIRE",
    role: "PROJECT LEAD",
    summary:
      "A custom character mod for the roguelike deckbuilder Slay the Spire — a full playable class with its own cards, relics, mechanics, and events.",
    description: [
      "Built with Java, ModTheSpire and BaseMod: a brand new character whose deck, keywords, and relics form their own self-contained system.",
      "Shipped with custom art, full audio, branching events, and a special ending.",
    ],
    highlights: [
      "Brand new character with unique mechanics.",
      "Custom arts, full audio, and special ending.",
    ],
    tags: ["JAVA", "SLAY THE SPIRE", "GAMEPLAY", "GAME DESIGN"],
    credits: [
      { label: "DESIGN / ART", value: "SARAH" },
      { label: "OTHERS", value: "RUMIA" },
    ],
    thanks: [
      "QingMu_",
      "Ubye Fighter",
      "xe08",
      "Slay the Spire Chinese Mod Community",
      "ModTheSpire",
      "BaseMod",
      "ActLikeIt",
      "All internal testers!",
    ],
    icon: "/projects/sakiko-icon.webp",
    key: {
      src: "/projects/sakiko-key.webp",
      alt: "Togawa Sakiko mod key art — a custom Slay the Spire title screen",
    },
    gallery: [
      {
        src: "/projects/sakiko-1.webp",
        alt: "Character select — Togawa Sakiko with her starting relic Monochrome Hairband",
      },
      {
        src: "/projects/sakiko-2.webp",
        alt: "The custom card pool — attacks, skills and powers",
      },
      {
        src: "/projects/sakiko-3.webp",
        alt: "Card detail — MementoMori, with custom Purge and Divinity keywords",
      },
      {
        src: "/projects/sakiko-4.webp",
        alt: "Custom event — Choose your Destiny, with branching outcomes",
      },
      {
        src: "/projects/sakiko-5.webp",
        alt: "Combat — Sakiko against a custom boss encounter",
      },
      {
        src: "/projects/sakiko-6.webp",
        alt: "Ending illustration — Sakiko at the piano and on stage",
      },
    ],
    links: [
      {
        label: "STEAM WORKSHOP",
        href: "https://steamcommunity.com/sharedfiles/filedetails/?id=3359249284",
      },
    ],
    showInTabs: true,
  },
  {
    index: "03",
    slug: "magic-candy-shooter",
    tabLabel: "CANDY SHOOTER",
    title: "Magic Candy Shooter",
    subtitle: "Halloween pixel-art brick breaker",
    status: "RELEASED",
    year: "2026",
    engine: "UNITY / C#",
    role: "PROJECT LEAD",
    summary:
      "A pixel-art brick breaker in a haunted graveyard. Your home is surrounded by candies! Use magic balls to break them before they break in!",
    description: [
      "The original game idea is borrowed from popular brick breaker games like Ballz.",
      "Featuring the character Limulady from \"FNF: Late Night City Tales\". We hope she brings you a chill and wonderful Halloween!",
      "Playable on PC (Windows) and in the browser, in English, 简体中文 and 日本語.",
    ],
    highlights: [
      "Aim with the mouse and click to fire. Break the candy bricks before they reach the bottom.",
      "Spend candy on more balls: Buy 1 for 5 candy, Buy 5 for 23.",
      "Press the skill button to cast Limulady's Magic Blast. It hits the lowest brick in every column and recharges over seven levels.",
      "When the game is over, your score turns into Coins. Spend them in the Shop on permanent upgrades.",
      "Try to reach the highest score!",
    ],
    tags: ["UNITY", "2D", "C#", "PIXEL ART", "ARCADE", "BREAKOUT", "HALLOWEEN"],
    credits: [
      { label: "IN-GAME ART", value: "LOCKIE" },
      { label: "PROMO ART", value: "CLEARCATX" },
      { label: "LOGO DESIGN", value: "SARAH" },
      { label: "MUSIC", value: "ATMGEEE" },
    ],
    icon: "/projects/magic-candy-icon.webp",
    key: {
      src: "/projects/magic-candy-key.webp",
      alt: "Magic Candy Shooter key art — Limulady with her stitched bear, surrounded by ghosts and candy, with the game logo",
    },
    videos: [{ youtubeId: "9SgRsH_CDNE", title: "Magic Candy Shooter Showcase" }],
    gallery: [
      {
        src: "/projects/magic-candy-1.webp",
        alt: "Main menu — aim a ball at Start, Shop, Credit, or Option in the graveyard",
      },
      {
        src: "/projects/magic-candy-2.webp",
        alt: "Combat — a 458-hit combo blasts through the candy bricks on wave 53",
      },
      {
        src: "/projects/magic-candy-3.webp",
        alt: "Combat — a 332-hit combo of fire and ice across the board",
      },
      {
        src: "/projects/magic-candy-4.webp",
        alt: "Combat — a Magical! callout as Magic Blast charges to full",
      },
      {
        src: "/projects/magic-candy-5.webp",
        alt: "Magic Blast — ghostly flames strike the lowest brick in every column",
      },
      {
        src: "/projects/magic-candy-6.webp",
        alt: "Shop — spend Coins on permanent upgrades like Lucky Charm and Swift Spell",
      },
    ],
    links: [
      {
        label: "ITCH.IO",
        href: "https://projectdespair.itch.io/magic-candy-shooter",
        icon: "itch",
      },
    ],
    showInTabs: true,
    webgl: {
      enabled: true,
      src: "/webgl/magic-candy-shooter/",
      aspectRatio: "16 / 9",
    },
  },
];
